import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OrderLabelInput } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import type { ProductionBoardSearch } from '../schemas/production-board-search';

const ALL = '__all__';

export interface BoardToolbarProps {
  search: ProductionBoardSearch;
  stages: Array<{ key: string; name: string }>;
  onChange: (patch: Partial<ProductionBoardSearch>) => void;
}

/** Stage, label and order search; the URL holds them so a filtered board can be shared. */
export function BoardToolbar({ search, stages, onChange }: BoardToolbarProps) {
  const { t } = useTranslation('production');
  const [q, setQ] = useState(search.q ?? '');
  const [syncedQ, setSyncedQ] = useState(search.q ?? '');
  if (syncedQ !== (search.q ?? '')) {
    setSyncedQ(search.q ?? '');
    setQ(search.q ?? '');
  }

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed === (search.q ?? '')) return;
    const id = window.setTimeout(() => onChange({ q: trimmed || undefined }), 300);
    return () => window.clearTimeout(id);
  }, [q, search.q, onChange]);

  const filtered = [search.q, search.stage, search.label].some(Boolean);

  return (
    <div className="flex flex-wrap items-center gap-2 pb-3">
      <div className="relative w-full sm:max-w-xs" data-tour="production.board.search">
        <Search
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
          aria-hidden
        />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('board.search')}
          aria-label={t('board.search')}
          className="pl-8"
        />
      </div>
      <Select
        value={search.stage ?? ALL}
        onValueChange={(value) => onChange({ stage: value === ALL ? undefined : value })}
      >
        <SelectTrigger className="w-[180px]" aria-label={t('board.stageFilter')}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('board.allStages')}</SelectItem>
          {stages.map((stage) => (
            <SelectItem key={stage.key} value={stage.key}>
              {stage.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={search.label ?? ALL}
        onValueChange={(value) =>
          onChange({ label: value === ALL ? undefined : (value as OrderLabelInput) })
        }
      >
        <SelectTrigger
          className="w-[210px]"
          aria-label={t('board.labelFilter')}
          data-tour="production.board.label-filter"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('board.allLabels')}</SelectItem>
          {Object.values(OrderLabelInput).map((label) => (
            <SelectItem key={label} value={label}>
              {t(`label.${label}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {filtered ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange({ q: undefined, stage: undefined, label: undefined })}
        >
          {t('board.clear')}
        </Button>
      ) : null}
    </div>
  );
}
